import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Flex, FormControl, Icon, IconButton, Image, SimpleGrid, Spinner, Text, VStack, useToast,
} from '@chakra-ui/react';
import { FiCheck, FiCopy, FiInfo, FiX } from 'react-icons/fi';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Bounty, BountyClaim, BountyIntroChannel } from '@foodfluence/shared';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { useBountyData } from '../../../contexts/BountyDataContext';
import { bountyApi } from '../../../services/api/bounties';
import { ff } from '../../../theme';
import { DisplayHeading } from '../../../components/ui';
import BountyBackButton from '../components/BountyBackButton';
import BrandTile from '../components/BrandTile';
import IntroContactFormFields from '../components/IntroContactFormFields';
import Pill from '../components/Pill';
import SalesContactCard from '../components/SalesContactCard';
import StickyBottomCta from '../components/StickyBottomCta';
import claimStart from '../assets/claim-start.png';
import claimWarning from '../assets/claim-warning.png';
import claimPaid from '../assets/claim-paid.png';
import claimNotSelected from '../assets/claim-not-selected.png';
import successIcon from '../assets/success.png';
import { CLAIM_DISCLAIMER, NOT_SELECTED_COPY, QUALIFIED_MEETING_COPY, SALES_CONTACT } from '../constants';
import {
  claimToIntroFormValues, formatIntroContactSummary, INTRO_CHANNEL_LABELS, IntroFieldKey, IntroFormValues,
  hasIntroContactMethod, introFormToContact, validateIntroForm,
} from '../introFormUtils';
import {
  bountyMetaLine, claimRoutePath, claimStatusChip, introMessageTemplate, myBountiesPath, resubmitIntroPath,
} from '../utils';
import { bountyColors, colorSchemeToTone, eyebrowStyle, pageBottomPaddingWithStickyCta } from '../theme';

/**
 * One stepped page for the whole claim flow. The `:step` URL segment keeps the old per-page
 * URLs (claimed | instructions | submit | resubmit | submitted) so claimRoutePath, SMS links
 * and bookmarks land on the matching step.
 */
type FlowStep = 'claimed' | 'instructions' | 'submit' | 'resubmit' | 'submitted';

const FLOW_STEPS: FlowStep[] = ['claimed', 'instructions', 'submit', 'resubmit', 'submitted'];
const isFlowStep = (value: string | undefined): value is FlowStep =>
  FLOW_STEPS.includes(value as FlowStep);

const PROGRESS_NAMES = ['Claimed', 'Make the intro', 'Your contact', 'Done'];
/** 1-based position in the 4-step bar; "Claimed" is already done when the page opens. */
const PROGRESS_STEP: Record<FlowStep, number> = {
  claimed: 2, instructions: 2, submit: 3, resubmit: 3, submitted: 4,
};

const isIntroChannel = (value: string | null): value is BountyIntroChannel =>
  value !== null && value in INTRO_CHANNEL_LABELS;

const SALES_FIRST_NAME = SALES_CONTACT.name.split(' ')[0];

const CHANNELS: { key: BountyIntroChannel; label: string; hint: string }[] = [
  { key: 'email', label: 'Email thread', hint: `CC ${SALES_FIRST_NAME} on an email` },
  { key: 'sms', label: 'iMessage / SMS', hint: 'Start a group text' },
  { key: 'whatsapp', label: 'WhatsApp', hint: `Add ${SALES_FIRST_NAME} to a chat` },
  { key: 'dm', label: 'Instagram', hint: 'Share their handle' },
];

const TEMPLATE_LABELS: Record<BountyIntroChannel, { title: string; open: string }> = {
  email: { title: 'Intro email', open: 'Open in Mail' },
  sms: { title: 'Intro text', open: 'Open Messages' },
  whatsapp: { title: 'Intro message', open: 'Open WhatsApp' },
  dm: { title: 'Intro DM', open: 'Open Instagram' },
};

const stepPath = (claimId: string, step: FlowStep, channel?: BountyIntroChannel) =>
  `/bounties/claims/${claimId}/${step}${channel ? `?channel=${channel}` : ''}`;

const StepImage: React.FC<{ src: string }> = ({ src }) => (
  <Image src={src} alt="" w="64px" h="64px" objectFit="contain" flex="none" />
);

const PrimaryCta: React.FC<{
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}> = ({ label, onClick, isLoading, isDisabled }) => (
  <Button
    variant="lime"
    w="full"
    h="56px"
    borderRadius="full"
    fontSize="16px"
    _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
    isLoading={isLoading}
    isDisabled={isDisabled}
    onClick={onClick}
  >
    {label}
  </Button>
);

const NumberedSteps: React.FC<{ steps: { title: string; body: string }[] }> = ({ steps }) => (
  <Box as="ol" listStyleType="none" m={0} p={0}>
    {steps.map((step, i) => (
      <Flex as="li" key={step.title} gap="14px" pb={i === steps.length - 1 ? 0 : '18px'}>
        <Flex
          flex="none"
          w="32px"
          h="32px"
          borderRadius="full"
          bg={i === 0 ? ff.ink : '#F0F0EE'}
          color={i === 0 ? ff.lime : ff.ink}
          align="center"
          justify="center"
          fontSize="14px"
          fontWeight={600}
        >
          {i + 1}
        </Flex>
        <Box>
          <Text as="strong" display="block" fontSize="16px" fontWeight={600}>{step.title}</Text>
          <Text fontSize="14px" color="#55555A" mt="2px">{step.body}</Text>
        </Box>
      </Flex>
    ))}
  </Box>
);

/* ---------- Step: Claimed → pick how to make the intro ---------- */

const ClaimedStep: React.FC<{ claim: BountyClaim; initialChannel: BountyIntroChannel | null }> = ({ claim, initialChannel }) => {
  const navigate = useNavigate();
  const [picked, setPicked] = useState<BountyIntroChannel>(initialChannel ?? 'email');

  return (
    <>
      <VStack spacing="20px" align="stretch">
        <VStack spacing="12px" align="flex-start">
          <StepImage src={claimStart} />
          <DisplayHeading size="29px">You&apos;ve claimed this bounty!</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.4" color="#55555A">
            {`Now introduce your contact at ${claim.companyName} and you're one step closer to getting paid.`}
          </Text>
        </VStack>
        <Flex gap="10px" p="14px" borderRadius="14px" bg={bountyColors.amberTint} color="#5C3900" fontSize="14px" lineHeight="1.4">
          <Image src={claimWarning} alt="" w="22px" h="22px" objectFit="contain" flex="none" />
          <Text>{CLAIM_DISCLAIMER}</Text>
        </Flex>
        <VStack spacing="10px" align="stretch">
          <Text as="h2" fontSize="20px" fontWeight={600}>Introduce your contact to us</Text>
          <Text fontSize="14px" color="#55555A">
            Choose the way that works best for you. Once we connect with them, we&apos;ll handle the rest.
          </Text>
          <SimpleGrid role="radiogroup" aria-label="Intro channel" columns={2} spacing="10px">
            {CHANNELS.map(channel => {
              const on = picked === channel.key;
              return (
                <Flex
                  key={channel.key}
                  as="button"
                  type="button"
                  role="radio"
                  aria-checked={on}
                  direction="column"
                  justify="center"
                  gap="4px"
                  minH="72px"
                  p="12px"
                  textAlign="left"
                  borderRadius="16px"
                  border={on ? '2px solid' : '1px solid'}
                  borderColor={on ? ff.ink : ff.border}
                  bg={on ? ff.limeSurface : 'white'}
                  color={ff.ink}
                  onClick={() => setPicked(channel.key)}
                >
                  <Text as="span" fontSize="15px" fontWeight={600}>{channel.label}</Text>
                  <Text as="span" fontSize="12px" color="#55555A">{channel.hint}</Text>
                </Flex>
              );
            })}
          </SimpleGrid>
        </VStack>
      </VStack>
      <StickyBottomCta>
        <PrimaryCta label="Continue" onClick={() => navigate(stepPath(claim.id, 'instructions', picked))} />
      </StickyBottomCta>
    </>
  );
};

/* ---------- Step: Make the intro → template, copy, open the app ---------- */

const InstructionsStep: React.FC<{ claim: BountyClaim; channel: BountyIntroChannel }> = ({ claim, channel }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const companyName = claim.companyName;
  const template = introMessageTemplate(channel, SALES_FIRST_NAME);
  const subject = `Intro: Foodfluence x ${companyName}`;

  const mailtoHref = `mailto:?cc=${SALES_CONTACT.email}` +
    `&subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(template)}`;
  const smsHref = `sms:${SALES_CONTACT.phone}`;
  const openHref: Record<BountyIntroChannel, string> = {
    email: mailtoHref,
    sms: smsHref,
    whatsapp: SALES_CONTACT.whatsappUrl,
    dm: SALES_CONTACT.instagramUrl,
  };
  const opensNewTab = channel === 'whatsapp' || channel === 'dm';

  const copyTemplate = async () => {
    await navigator.clipboard.writeText(template);
    setCopied(true);
    toast({ title: 'Template copied', status: 'success', duration: 2000 });
  };

  const steps = [
    { title: 'Reach out to your contact', body: `Introduce Foodfluence to your contact at ${companyName} on your chosen channel.` },
    { title: `Loop in ${SALES_CONTACT.name}`, body: `Add ${SALES_CONTACT.name} (${SALES_CONTACT.title}) to the thread, or submit the contact's info here and we'll reach out.` },
    { title: 'Qualified meeting happens', body: `${SALES_CONTACT.name} books a qualified meeting with your contact.` },
    { title: 'Get paid', body: 'Once the meeting is verified, the bounty is paid to your Whop wallet.' },
  ];

  return (
    <>
      <VStack spacing="22px" align="stretch">
        <DisplayHeading size="29px">Here&apos;s what to do next</DisplayHeading>

        <Box border="1px solid" borderColor="#E4E4E0" borderRadius="18px" overflow="hidden">
          <Flex px="14px" py="12px" bg={ff.grey} align="center" justify="space-between" gap="10px">
            <Text {...eyebrowStyle} fontSize="12px" color="#55555A">{TEMPLATE_LABELS[channel].title}</Text>
            <Button
              variant="ghost"
              h="44px"
              px="12px"
              borderRadius="0"
              fontSize="13px"
              fontWeight={600}
              color={ff.ink}
              leftIcon={<Icon as={FiCopy} boxSize="16px" />}
              iconSpacing="6px"
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              aria-label="Copy template"
              onClick={() => void copyTemplate()}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Flex>
          <VStack spacing="10px" align="stretch" p="14px" fontSize="14px" lineHeight="1.5" color="#2A2A2C">
            {channel === 'email' && (
              <Text><Text as="strong">Subject:</Text> {subject}</Text>
            )}
            <Text whiteSpace="pre-line">{template}</Text>
          </VStack>
          <Box px="14px" pb="14px">
            <Button
              as="a"
              href={openHref[channel]}
              target={opensNewTab ? '_blank' : undefined}
              rel={opensNewTab ? 'noopener noreferrer' : undefined}
              variant="ink"
              w="full"
              h="48px"
              borderRadius="full"
              fontSize="15px"
              color={ff.lime}
            >
              {TEMPLATE_LABELS[channel].open}
            </Button>
          </Box>
        </Box>

        <NumberedSteps steps={steps} />

        <SalesContactCard />

        <Flex gap="8px" align="center" p="12px" borderRadius="14px" bg={ff.grey}>
          <Icon as={FiInfo} boxSize="16px" color={ff.text2} flex="none" />
          <Text fontSize="13px" color="#3E3E42">{QUALIFIED_MEETING_COPY}</Text>
        </Flex>
      </VStack>
      <StickyBottomCta>
        <PrimaryCta label="I've made the intro" onClick={() => navigate(stepPath(claim.id, 'submit', channel))} />
      </StickyBottomCta>
    </>
  );
};

/* ---------- Step: Your contact (first submit) ---------- */

const EMPTY_INTRO: IntroFormValues = { name: '', role: '', email: '', phone: '', instagramHandle: '', note: '' };

const SubmitStep: React.FC<{ claim: BountyClaim; channel: BountyIntroChannel }> = ({ claim, channel }) => {
  const { influencerData } = useContext(InfluencerDataContext);
  const { refetchBounties } = useBountyData();
  const navigate = useNavigate();
  const toast = useToast();
  const [values, setValues] = useState<IntroFormValues>(EMPTY_INTRO);
  const [errors, setErrors] = useState<Partial<Record<IntroFieldKey, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (key: IntroFieldKey, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const isValid = Boolean(values.name.trim() && values.role.trim() && hasIntroContactMethod(values));

  const handleSubmit = async () => {
    if (!influencerData?.id) return;
    const found = validateIntroForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSubmitting(true);
    try {
      await bountyApi.submitIntro(influencerData.id, claim.id, {
        channel,
        contact: introFormToContact(values),
        ...(values.note.trim() ? { note: values.note.trim() } : {}),
      });
      await refetchBounties();
      navigate(stepPath(claim.id, 'submitted'), { replace: true });
    } catch (error) {
      toast({
        title: 'Could not submit intro',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <VStack spacing="18px" align="stretch">
        <Box>
          <DisplayHeading size="29px">Submit your contact</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.4" color="#55555A" mt="8px">
            {`Tell us who to talk to at ${claim.companyName}.`} We&apos;ll take it from here.
          </Text>
        </Box>
        <FormControl as="fieldset" border="none" p={0} m={0}>
          <VStack spacing="18px" align="stretch">
            <IntroContactFormFields values={values} errors={errors} onChange={handleChange} />
          </VStack>
        </FormControl>
      </VStack>
      <StickyBottomCta>
        <PrimaryCta label="Submit Intro" isDisabled={!isValid} isLoading={isSubmitting} onClick={() => void handleSubmit()} />
      </StickyBottomCta>
    </>
  );
};

/* ---------- Step: Your contact (resubmit after the intro was rejected) ---------- */

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Flex gap="12px" py="10px" borderTop="1px solid" borderColor={ff.line}>
    <Text w="120px" flexShrink={0} fontSize="13px" color={ff.text2} lineHeight="1.4">{label}</Text>
    <Text flex={1} minW={0} fontSize="14px" fontWeight={600} lineHeight="1.4" wordBreak="break-word" color={ff.ink}>{value}</Text>
  </Flex>
);

const ResubmitStep: React.FC<{ claim: BountyClaim }> = ({ claim }) => {
  const { influencerData } = useContext(InfluencerDataContext);
  const { refetchBounties } = useBountyData();
  const navigate = useNavigate();
  const toast = useToast();
  const [values, setValues] = useState<IntroFormValues>(() => claimToIntroFormValues(claim));
  const [errors, setErrors] = useState<Partial<Record<IntroFieldKey, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setValues(claimToIntroFormValues(claim));
  }, [claim]);

  const handleChange = (key: IntroFieldKey, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = async () => {
    if (!influencerData?.id) return;
    const found = validateIntroForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const channel = (claim.channel ?? 'email') as BountyIntroChannel;
    setIsSubmitting(true);
    try {
      await bountyApi.submitIntro(influencerData.id, claim.id, {
        channel,
        contact: introFormToContact(values),
        ...(values.note.trim() ? { note: values.note.trim() } : {}),
      });
      await refetchBounties();
      toast({
        title: 'Intro resubmitted',
        description: 'We\'ll review the updated contact info and follow up.',
        status: 'success', duration: 5000, isClosable: true,
      });
      navigate(myBountiesPath, { replace: true });
    } catch (error) {
      toast({
        title: 'Could not resubmit intro',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contact = claim.contact;
  const channelLabel = claim.channel ? INTRO_CHANNEL_LABELS[claim.channel] : '—';

  return (
    <>
      <VStack spacing="18px" align="stretch">
        <Box>
          <DisplayHeading size="29px">Resubmit intro to {claim.companyName}</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.4" color="#55555A" mt="8px">
            Your previous contact couldn&apos;t be verified. Review what you sent, update anything that&apos;s wrong, and resubmit.
          </Text>
        </Box>

        {claim.rejectionReason && (
          <Box px="14px" py="12px" borderRadius="14px" bg={bountyColors.redTint} color={bountyColors.redInk} fontSize="14px" lineHeight="1.4">
            <Text as="strong">Why we need new info:</Text> {claim.rejectionReason}
          </Box>
        )}

        {contact && (
          <Box border="1px solid" borderColor="#E4E4E0" borderRadius="18px" px="16px" pt="16px" pb="4px">
            <Text as="h2" {...eyebrowStyle} pb="10px">What you submitted</Text>
            <SummaryRow label="Contact" value={`${contact.name} · ${contact.role}`} />
            <SummaryRow label="How to reach them" value={formatIntroContactSummary(contact)} />
            <SummaryRow label="Intro channel" value={channelLabel} />
            {claim.note && <SummaryRow label="Notes" value={claim.note} />}
          </Box>
        )}

        <Box>
          <Text as="h2" {...eyebrowStyle}>Update contact info</Text>
          <Text fontSize="14px" color={ff.text2} mt="4px" mb="16px">
            Fields are prefilled from your last submission — change only what needs fixing.
          </Text>
          <VStack spacing="18px" align="stretch">
            <IntroContactFormFields values={values} errors={errors} onChange={handleChange} />
          </VStack>
        </Box>
      </VStack>
      <StickyBottomCta>
        <PrimaryCta label="Resubmit Intro" isLoading={isSubmitting} onClick={() => void handleSubmit()} />
      </StickyBottomCta>
    </>
  );
};

/* ---------- Step: Done → in review / paid / went to someone else ---------- */

const DoneStep: React.FC<{ claim: BountyClaim; amount: number }> = ({ claim, amount }) => {
  const navigate = useNavigate();
  const isVerified = claim.status === 'verified';
  const isNotSelected = claim.status === 'notSelected';
  const chip = claimStatusChip(claim.status);
  const payout = claim.payoutAmount ?? amount;

  if (isVerified) {
    return (
      <>
        <VStack spacing="16px" align="stretch">
          <StepImage src={claimPaid} />
          <DisplayHeading size="36px">You got paid!</DisplayHeading>
          <Flex direction="column" gap="6px" px="20px" py="24px" borderRadius="24px" bg={ff.ink} color="white">
            <Text textStyle="eyebrow" fontSize="11px" letterSpacing="0.1em" color={ff.faint}>Bounty payout</Text>
            <Text textStyle="display" fontSize="40px" color={ff.lime}>${payout}</Text>
            <Text fontSize="14px" color={ff.faint}>
              {claim.payoutStatus === 'paid' ? 'Sent to your Whop wallet' : 'On its way to your Whop wallet'}
            </Text>
          </Flex>
          <Text fontSize="15px" lineHeight="1.45" color="#3E3E42">
            {`Your intro to ${claim.companyName} was verified — $${payout} is on its way to your Whop wallet.`}
          </Text>
        </VStack>
        <StickyBottomCta>
          <PrimaryCta label="Open Wallet" onClick={() => navigate('/wallet')} />
        </StickyBottomCta>
      </>
    );
  }

  if (isNotSelected) {
    return (
      <>
        <VStack spacing="16px" align="stretch">
          <Image src={claimNotSelected} alt="" w="64px" h="64px" objectFit="contain" flex="none" />
          <DisplayHeading size="31px">This one went to someone else</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.45" color="#3E3E42">{NOT_SELECTED_COPY}</Text>
        </VStack>
        <StickyBottomCta>
          <PrimaryCta label="Find another bounty" onClick={() => navigate('/bounties')} />
        </StickyBottomCta>
      </>
    );
  }

  const timeline = [
    { title: "We'll reach out", body: `${SALES_CONTACT.name} will contact them within 1-2 business days.` },
    { title: 'Qualified meeting', body: `If they're a good fit, ${SALES_CONTACT.name} schedules a qualified meeting.` },
    { title: 'You get paid', body: 'Once the meeting is verified, the bounty is paid to your Whop wallet.' },
  ];

  return (
    <>
      <VStack spacing="20px" align="stretch">
        <StepImage src={successIcon} />
        <Box>
          <DisplayHeading size="36px">Nice work!</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.45" color="#3E3E42" mt="10px">
            {`You've introduced your contact at ${claim.companyName}. We'll take it from here.`}
          </Text>
          <Box mt="12px">
            <Pill label={`Status: ${chip.label}`} tone={colorSchemeToTone(chip.colorScheme)} />
          </Box>
        </Box>
        <Text as="h2" {...eyebrowStyle}>What happens next?</Text>
        <VStack as="ol" listStyleType="none" m={0} p={0} spacing="16px" align="stretch">
          <Flex as="li" gap="12px" align="flex-start">
            <Flex flex="none" w="24px" h="24px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" justify="center">
              <Icon as={FiCheck} boxSize="14px" strokeWidth={3} />
            </Flex>
            <Text as="strong" fontWeight={600}>Contact received</Text>
          </Flex>
          {timeline.map((item, i) => (
            <Flex as="li" key={item.title} gap="12px" align="flex-start">
              <Box flex="none" w="24px" h="24px" borderRadius="full" border="2px solid" borderColor={i === 0 ? ff.ink : ff.faint} />
              <Box>
                <Text as="strong" display="block" fontWeight={600}>{item.title}</Text>
                <Text fontSize="14px" color="#55555A" mt="2px">{item.body}</Text>
              </Box>
            </Flex>
          ))}
        </VStack>
        <Text fontSize="13px" color={ff.text2}>
          You&apos;ll get a text at every step — we&apos;ll keep you updated along the way.
        </Text>
      </VStack>
      <StickyBottomCta>
        <PrimaryCta label="Back to Bounties" onClick={() => navigate('/bounties')} />
      </StickyBottomCta>
    </>
  );
};

/* ---------- Page ---------- */

const ClaimSummaryCard: React.FC<{ claim: BountyClaim; bounty?: Bounty; amount: number }> = ({ claim, bounty, amount }) => {
  const meta = bounty ? bountyMetaLine(bounty) : '';
  return (
    <Flex mx="20px" mt="14px" p="12px" gap="12px" align="center" borderRadius="16px" bg={ff.grey}>
      <BrandTile name={claim.companyName} logoUrl={bounty?.logoUrl} size={44} radius={12} fontSize={14} />
      <Flex direction="column" gap="2px" flex={1} minW={0}>
        <Text fontSize="16px" fontWeight={600} noOfLines={1}>{claim.companyName}</Text>
        {meta && <Text fontSize="13px" color="#55555A" noOfLines={1}>{meta}</Text>}
      </Flex>
      <Flex direction="column" align="flex-end" flex="none">
        <Text textStyle="display" fontSize="26px">${amount}</Text>
        <Text textStyle="eyebrow" fontSize="10px" letterSpacing="0.08em" color="#55555A">Payout</Text>
      </Flex>
    </Flex>
  );
};

const BountyClaimFlowPage: React.FC = () => {
  const { claimId, step: stepParam } = useParams<{ claimId: string; step: string }>();
  const [searchParams] = useSearchParams();
  const channelParam = searchParams.get('channel');
  const chosenChannel = isIntroChannel(channelParam) ? channelParam : null;
  const channel: BountyIntroChannel = chosenChannel ?? 'email';
  const { claims, claimsLoading, bounties } = useBountyData();
  const navigate = useNavigate();

  const claim = claims.find(c => c.id === claimId);
  const bounty = claim ? bounties.find(b => b.id === claim.bountyID) : undefined;
  const step = isFlowStep(stepParam) ? stepParam : null;

  // Redirects that used to live on the individual pages.
  const redirectTo = (() => {
    if (claimsLoading || !claimId) return null;
    if (step === 'resubmit') return claim?.status === 'introRejected' ? null : myBountiesPath;
    if (!claim) return null;
    if (!step) return claimRoutePath(claim);
    if (step === 'submit' && claim.status === 'introRejected') return resubmitIntroPath(claim.id);
    return null;
  })();

  useEffect(() => {
    if (redirectTo) navigate(redirectTo, { replace: true });
  }, [redirectTo, navigate]);

  if (claimsLoading || redirectTo) {
    return (
      <Flex minH="100svh" align="center" justify="center" bg="white">
        <Spinner color={ff.muted} />
      </Flex>
    );
  }

  if (!claim || !step) {
    return (
      <Flex minH="100svh" bg="white" fontFamily="display" color={ff.ink} lineHeight="normal" align="center" justify="center" p="16px">
        <VStack spacing="16px">
          <Text color={ff.text2}>Claim not found.</Text>
          <Button variant="ink" h="48px" px="24px" borderRadius="full" fontSize="15px" color={ff.lime} onClick={() => navigate('/bounties')}>
            Back to Bounties
          </Button>
        </VStack>
      </Flex>
    );
  }

  const progress = PROGRESS_STEP[step];
  const isResubmit = step === 'resubmit';
  const amount = bounty?.amount ?? claim.payoutAmount ?? 0;
  const back: { to: string; replace?: boolean; label: string } = {
    claimed: { to: '/bounties', label: 'Back to bounties' },
    instructions: { to: stepPath(claim.id, 'claimed', channel), label: 'Previous step' },
    submit: { to: stepPath(claim.id, 'instructions', channel), label: 'Previous step' },
    resubmit: { to: myBountiesPath, replace: true, label: 'Back to My Bounties' },
    submitted: { to: '/bounties', label: 'Back to bounties' },
  }[step];

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink} lineHeight="normal" pt="12px" pb={pageBottomPaddingWithStickyCta} overflowX="hidden">
      <Flex px="12px" align="center" gap="8px">
        <BountyBackButton to={back.to} replace={back.replace} aria-label={back.label} />
        <Flex direction="column" gap="6px" flex={1} minW={0}>
          <Text textStyle="eyebrow" fontSize="12px" letterSpacing="0.08em" color="#55555A">
            {isResubmit ? 'Update contact' : `Step ${progress} of 4 · ${PROGRESS_NAMES[progress - 1]}`}
          </Text>
          <Flex gap="4px" aria-hidden>
            {PROGRESS_NAMES.map((name, i) => (
              <Box key={name} flex={1} h="4px" borderRadius="full" bg={i < progress ? ff.ink : '#E4E4E0'} />
            ))}
          </Flex>
        </Flex>
        <IconButton
          aria-label="Close"
          icon={<Icon as={FiX} boxSize="18px" strokeWidth={2.4} />}
          variant="ghost"
          flex="none"
          w="44px"
          h="44px"
          minW="44px"
          borderRadius="full"
          color={ff.ink}
          _hover={{ bg: ff.grey }}
          _active={{ bg: ff.grey }}
          onClick={() => navigate('/bounties')}
        />
      </Flex>

      <ClaimSummaryCard claim={claim} bounty={bounty} amount={amount} />

      <Box px="20px" pt="24px">
        {step === 'claimed' && <ClaimedStep claim={claim} initialChannel={chosenChannel} />}
        {step === 'instructions' && <InstructionsStep key={channel} claim={claim} channel={channel} />}
        {step === 'submit' && <SubmitStep claim={claim} channel={channel} />}
        {step === 'resubmit' && <ResubmitStep claim={claim} />}
        {step === 'submitted' && <DoneStep claim={claim} amount={amount} />}
      </Box>
    </Box>
  );
};

export default BountyClaimFlowPage;
